import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import Header from "@/components/Header";
import RenderOptionsPanel from "@/components/resume/RenderOptionsPanel";
import ResumeSection from "@/components/resume/ResumeSection";
import SkillsSection from "@/components/resume/SkillsSection";
import ExperienceSection from "@/components/resume/ExperienceSection";
import EducationSection from "@/components/resume/EducationSection";
import ShareDialog from "@/components/ShareDialog";
import CoBrandStrip from "@/components/CoBrandStrip";
import {
  ProjectsSection,
  CertificationsSection,
  LanguagesSection,
} from "@/components/resume/MiscSections";
import {
  Download,
  ArrowLeft,
  Trash2,
  Edit3,
  Save,
  X,
  FileType,
  Sparkles,
  Layout,
  Share2,
} from "lucide-react";
import { toast } from "sonner";
import {
  getResume,
  deleteResume,
  updateResume,
  pdfUrl,
  docxUrl,
  logoUrl,
  resummarizeResume,
  listTemplates,
  getAppSettings,
} from "@/lib/api";

export default function ResumeDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [resume, setResume] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [saving, setSaving] = useState(false);
  const [resummarizing, setResummarizing] = useState(false);

  // Render overrides (per-resume; default to global settings)
  const [templates, setTemplates] = useState([]);
  const [renderTemplate, setRenderTemplate] = useState("modern");
  const [renderAnonymize, setRenderAnonymize] = useState(false);
  const [renderShowBullets, setRenderShowBullets] = useState(true);
  const [showPicker, setShowPicker] = useState(false);
  const [showShare, setShowShare] = useState(false);

  // Initial load (id is the only true dep; navigate is stable)
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [data, tpls, gs] = await Promise.all([
          getResume(id),
          listTemplates().catch(() => []),
          getAppSettings().catch(() => null),
        ]);
        if (cancelled) return;
        setResume(data);
        setTemplates(tpls);
        if (gs) {
          setRenderTemplate(gs.template || "modern");
          setRenderAnonymize(!!gs.anonymize);
          setRenderShowBullets(gs.show_bullets !== false);
        }
      } catch {
        if (!cancelled) {
          toast.error("Resume not found");
          navigate("/");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [id, navigate]);

  const startEdit = useCallback(() => {
    setDraft(JSON.parse(JSON.stringify(resume)));
    setEditing(true);
  }, [resume]);

  const cancelEdit = useCallback(() => {
    setEditing(false);
    setDraft(null);
  }, []);

  const save = useCallback(async () => {
    setSaving(true);
    try {
      const payload = {
        full_name: draft.full_name,
        professional_title: draft.professional_title,
        summary: draft.summary,
        skills: draft.skills,
        experience: draft.experience,
        education: draft.education,
        projects: draft.projects,
        certifications: draft.certifications,
        languages: draft.languages,
      };
      const updated = await updateResume(id, payload);
      setResume(updated);
      setEditing(false);
      setDraft(null);
      toast.success("Saved");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  }, [draft, id]);

  const handleDelete = useCallback(async () => {
    if (!window.confirm("Delete this resume?")) return;
    try {
      await deleteResume(id);
      toast.success("Deleted");
      navigate("/");
    } catch {
      toast.error("Delete failed");
    }
  }, [id, navigate]);

  const handleResummarize = useCallback(async () => {
    setResummarizing(true);
    try {
      const updated = await resummarizeResume(id);
      setResume(updated);
      toast.success("Experience summaries regenerated");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Re-summarize failed");
    } finally {
      setResummarizing(false);
    }
  }, [id]);

  // Stable setters for draft list ops
  const setField = useCallback((k, v) => {
    setDraft((d) => ({ ...d, [k]: v }));
  }, []);

  const updateListItem = useCallback((key, idx, next) => {
    setDraft((d) => {
      const list = [...(d[key] || [])];
      list[idx] = next;
      return { ...d, [key]: list };
    });
  }, []);

  const addListItem = useCallback((key, item) => {
    setDraft((d) => ({ ...d, [key]: [...(d[key] || []), item] }));
  }, []);

  const removeListItem = useCallback((key, idx) => {
    setDraft((d) => ({
      ...d,
      [key]: (d[key] || []).filter((_, i) => i !== idx),
    }));
  }, []);

  // Active view: draft when editing, otherwise stored resume
  const view = editing ? draft : resume;

  // Memoised download URLs (only re-compute when overrides change)
  const downloadOpts = useMemo(
    () => ({
      template: renderTemplate,
      anonymize: renderAnonymize,
      showBullets: renderShowBullets,
    }),
    [renderTemplate, renderAnonymize, renderShowBullets]
  );
  const hrefPdf = useMemo(() => pdfUrl(id, downloadOpts), [id, downloadOpts]);
  const hrefDocx = useMemo(() => docxUrl(id, downloadOpts), [id, downloadOpts]);

  if (loading) {
    return (
      <div className="min-h-screen">
        <Header />
        <div className="max-w-[1440px] mx-auto px-6 md:px-12 py-16">
          <div className="spinner-dark" />
        </div>
      </div>
    );
  }

  if (!view) return null;

  return (
    <div className="min-h-screen">
      <Header />
      <main className="max-w-[1440px] mx-auto px-6 md:px-12 py-10">
        {/* Toolbar */}
        <div className="flex flex-wrap items-center justify-between gap-3 mb-8">
          <Link to="/" className="btn-ghost" data-testid="back-to-dashboard-link">
            <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> BACK
          </Link>
          <div className="flex items-center gap-2">
            {!editing && (
              <>
                <button
                  type="button"
                  onClick={handleResummarize}
                  disabled={resummarizing}
                  className="btn-secondary"
                  data-testid="resummarize-button"
                  title="Regenerate AI summary for each role"
                >
                  {resummarizing ? (
                    <span className="spinner-dark" />
                  ) : (
                    <Sparkles className="w-4 h-4" strokeWidth={1.5} />
                  )}
                  RE-SUMMARIZE
                </button>
                <button
                  type="button"
                  onClick={startEdit}
                  className="btn-secondary"
                  data-testid="edit-resume-button"
                >
                  <Edit3 className="w-4 h-4" strokeWidth={1.5} /> EDIT
                </button>
                <button
                  type="button"
                  onClick={() => setShowPicker((v) => !v)}
                  className="btn-secondary"
                  data-testid="toggle-template-picker-button"
                  title="Choose template & render options"
                >
                  <Layout className="w-4 h-4" strokeWidth={1.5} />
                  TEMPLATE
                </button>
                <button
                  type="button"
                  onClick={() => setShowShare(true)}
                  className="btn-secondary"
                  data-testid="share-button"
                  title="Generate a public link to share this resume"
                >
                  <Share2 className="w-4 h-4" strokeWidth={1.5} />
                  SHARE
                </button>
                <a
                  href={hrefPdf}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-primary"
                  data-testid="download-pdf-button"
                >
                  <Download className="w-4 h-4" strokeWidth={1.5} /> DOWNLOAD PDF
                </a>
                <a
                  href={hrefDocx}
                  target="_blank"
                  rel="noreferrer"
                  className="btn-secondary"
                  data-testid="download-docx-button"
                >
                  <FileType className="w-4 h-4" strokeWidth={1.5} /> DOCX
                </a>
                <button
                  type="button"
                  onClick={handleDelete}
                  className="btn-ghost hover:text-[var(--brand-accent)]"
                  data-testid="delete-resume-button"
                >
                  <Trash2 className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </>
            )}
            {editing && (
              <>
                <button
                  type="button"
                  onClick={cancelEdit}
                  className="btn-secondary"
                  data-testid="cancel-edit-button"
                >
                  <X className="w-4 h-4" strokeWidth={1.5} /> CANCEL
                </button>
                <button
                  type="button"
                  onClick={save}
                  disabled={saving}
                  className="btn-primary"
                  data-testid="save-edit-button"
                >
                  {saving ? (
                    <span className="spinner" />
                  ) : (
                    <Save className="w-4 h-4" strokeWidth={1.5} />
                  )}
                  SAVE
                </button>
              </>
            )}
          </div>
        </div>

        {/* Template & render options (collapsible) */}
        {showPicker && !editing && (
          <RenderOptionsPanel
            templates={templates}
            template={renderTemplate}
            anonymize={renderAnonymize}
            showBullets={renderShowBullets}
            onTemplateChange={setRenderTemplate}
            onToggleAnonymize={() => setRenderAnonymize((v) => !v)}
            onToggleBullets={() => setRenderShowBullets((v) => !v)}
          />
        )}

        {/* Preview paper */}
        <div
          className="bg-white border border-[var(--border-default)] hard-shadow p-8 md:p-14 max-w-[920px] mx-auto fade-in"
          data-testid="resume-preview-paper"
        >
          {/* Top bar with logo */}
          <div className="flex items-start justify-between pb-5 border-b border-[var(--border-default)] mb-8">
            <div className="flex-1 pr-6">
              {editing ? (
                <>
                  <input
                    value={view.full_name || ""}
                    onChange={(e) => setField("full_name", e.target.value)}
                    placeholder="Full name"
                    className="swiss-input font-heading text-3xl md:text-4xl font-black tracking-tighter !p-2 mb-2"
                    data-testid="edit-full-name-input"
                  />
                  <input
                    value={view.professional_title || ""}
                    onChange={(e) =>
                      setField("professional_title", e.target.value)
                    }
                    placeholder="Professional title"
                    className="swiss-input !p-2"
                    data-testid="edit-title-input"
                  />
                </>
              ) : (
                <>
                  <h1
                    className="font-heading text-3xl md:text-5xl font-bold tracking-tight leading-none uppercase mb-2 text-[var(--text-primary)]"
                    data-testid="resume-name-heading"
                  >
                    {view.full_name || "Unnamed"}
                  </h1>
                  {view.professional_title && (
                    <p
                      className="text-base text-[var(--text-secondary)] font-normal"
                      data-testid="resume-title"
                    >
                      {view.professional_title}
                    </p>
                  )}
                </>
              )}
            </div>
            <div
              className="w-28 h-20 md:w-36 md:h-24 border border-[var(--border-default)] flex items-center justify-center shrink-0 overflow-hidden bg-white p-2"
              data-testid="resume-logo-corner"
            >
              <img
                src={logoUrl()}
                alt="company logo"
                className="max-w-full max-h-full object-contain"
                onError={(e) => {
                  e.currentTarget.style.display = "none";
                }}
              />
            </div>
          </div>

          {/* Summary */}
          {(view.summary || editing) && (
            <ResumeSection title="Summary" index={1}>
              {editing ? (
                <textarea
                  value={view.summary || ""}
                  onChange={(e) => setField("summary", e.target.value)}
                  rows={4}
                  className="swiss-input"
                  data-testid="edit-summary-input"
                />
              ) : (
                <p className="text-[15px] leading-relaxed text-[var(--text-primary)]">
                  {view.summary}
                </p>
              )}
            </ResumeSection>
          )}

          {((view.skills || []).length > 0 || editing) && (
            <SkillsSection
              skills={view.skills || []}
              editing={editing}
              onChange={(next) => setField("skills", next)}
            />
          )}

          {((view.experience || []).length > 0 || editing) && (
            <ExperienceSection
              experience={view.experience || []}
              editing={editing}
              onAdd={(item) => addListItem("experience", item)}
              onUpdate={(idx, next) => updateListItem("experience", idx, next)}
              onRemove={(idx) => removeListItem("experience", idx)}
            />
          )}

          {((view.education || []).length > 0 || editing) && (
            <EducationSection
              education={view.education || []}
              editing={editing}
              onAdd={(item) => addListItem("education", item)}
              onUpdate={(idx, next) => updateListItem("education", idx, next)}
              onRemove={(idx) => removeListItem("education", idx)}
            />
          )}

          {!editing && <ProjectsSection projects={view.projects || []} />}
          {!editing && (
            <CertificationsSection
              certifications={view.certifications || []}
            />
          )}
          {!editing && <LanguagesSection languages={view.languages || []} />}

          <div className="mt-8 pt-4 border-t border-[var(--border-default)] flex flex-col md:flex-row justify-between items-start md:items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-[var(--text-muted)]">
            <span>Standardized · contact data scrubbed</span>
            <span className="text-[var(--text-secondary)] font-sans text-[10pt] normal-case tracking-normal">
              ITHR Technologies Consulting LLC
            </span>
            <span>Page 01</span>
          </div>
        </div>
      </main>
      {showShare && (
        <ShareDialog resumeId={id} onClose={() => setShowShare(false)} />
      )}
      <CoBrandStrip className="mt-16" />
    </div>
  );
}
